
import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatModal } from '@/components/chat/ChatModal';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

interface ChatButtonProps {
  bookingId?: string;
  otherParticipantName?: string;
  currentUserType?: 'client' | 'practitioner';
  className?: string;
  size?: 'sm' | 'default';
  unreadCount?: number;
  onChatOpened?: () => void;
}

export function ChatButton({ 
  bookingId, 
  otherParticipantName = 'Other Party',
  currentUserType = 'client',
  className,
  size = 'sm',
  unreadCount: propUnreadCount,
  onChatOpened
}: ChatButtonProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { unreadCounts, markMessagesAsRead, refreshUnreadCounts } = useUnreadMessages({ 
    userId: undefined, // Will be set by the hook from auth context
    userType: currentUserType
  });
  const [displayUnreadCount, setDisplayUnreadCount] = useState(0);
  const [isMarking, setIsMarking] = useState(false);
  
  // Use the value from the hook if available, otherwise use the prop
  const effectiveUnreadCount = bookingId ? (unreadCounts.unreadByBooking[bookingId] || 0) : (propUnreadCount || 0);
  
  // Enhanced update local state when unread counts change
  useEffect(() => {
    console.log(`ChatButton[${bookingId}]: Updating display count from ${displayUnreadCount} to ${effectiveUnreadCount}`);
    setDisplayUnreadCount(effectiveUnreadCount);
  }, [effectiveUnreadCount, bookingId]);

  if (!bookingId) {
    return null;
  }

  const handleChatOpen = async () => {
    console.log(`ChatButton[${bookingId}]: Opening chat, current unread count: ${displayUnreadCount}`);
    setIsChatOpen(true);
    
    // Provide immediate visual feedback by setting count to 0
    if (bookingId && displayUnreadCount > 0) {
      console.log(`ChatButton[${bookingId}]: Providing immediate UI feedback - setting count to 0`);
      setDisplayUnreadCount(0);
      setIsMarking(true);
      
      try {
        console.log(`ChatButton[${bookingId}]: Starting to mark messages as read`);
        await markMessagesAsRead(bookingId);
        console.log(`ChatButton[${bookingId}]: Mark as read completed`);
        
        // Schedule multiple refreshes to ensure consistency
        const refreshDelays = [200, 1000, 3000];
        refreshDelays.forEach(delay => {
          setTimeout(() => {
            console.log(`ChatButton[${bookingId}]: Scheduled refresh at ${delay}ms`);
            refreshUnreadCounts();
          }, delay);
        });
      } catch (error) {
        console.error(`ChatButton[${bookingId}]: Error marking messages as read:`, error);
        // Revert the display count on error
        setDisplayUnreadCount(effectiveUnreadCount);
      } finally {
        setIsMarking(false);
      }
    }
    
    if (onChatOpened) {
      onChatOpened();
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={handleChatOpen}
        className={`relative ${className}`}
      >
        <MessageCircle className="h-4 w-4 md:mr-1" />
        <span className="hidden md:inline">Chat</span>
        {displayUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {displayUnreadCount > 99 ? '99+' : displayUnreadCount}
          </span>
        )}
      </Button>

      <ChatModal
        isOpen={isChatOpen}
        onClose={() => {
          console.log(`ChatButton[${bookingId}]: Chat modal closing`);
          setIsChatOpen(false);
          
          // Enhanced refresh strategy when modal closes
          const refreshDelays = [100, 500, 2000, 5000];
          refreshDelays.forEach(delay => {
            setTimeout(() => {
              console.log(`ChatButton[${bookingId}]: Post-close refresh at ${delay}ms`);
              refreshUnreadCounts();
            }, delay);
          });
        }}
        bookingId={bookingId}
        otherParticipantName={otherParticipantName}
        currentUserType={currentUserType}
      />
    </>
  );
}
